/**
 * yupバリデーションschemaのショートハンド
 */
import * as yup from 'yup'
import { transformToNumber } from 'ui/utils/PostalCodeToAddress'
import i18n from 'i18next'

export const text = () => yup.string().trim().max(100)

export const textRequired = () => text().nullable().required()

export const postalCode = () =>
  yup
    .string()
    .trim()
    .transform((string) => {
      return transformToNumber(string)
    })
    .max(7)

export const phone = () =>
  yup
    .string()
    .trim()
    .max(13)
    .matches(/^0(\d{1,4}-\d{1,4}-\d{3,4}|\d{9,10})$/, {
      message: i18n.t('validation.正しい形式にしてください'),
      excludeEmptyString: true,
    })

/**
 * SMS予約時 携帯電話番号のバリデーション
 *
 * Laravelで使用している
 * https://github.com/giggsey/libphonenumber-for-php/blob/master/src/PhoneNumberUtil.php
 * のisValidNumber()に準拠
 */
export const smsPhone = () =>
  yup
    .string()
    .trim()
    .test(
      'sms-phone-format',
      i18n.t('validation.正しい形式にしてください'),
      (value) => {
        if (typeof value !== 'string' || value === '') return true

        const normalizedValue = value.replace(/[-\s()]/g, '')
        return /^0(?:(?:1(?:1[235-8]|2[3-6]|3[3-9]|4[2-6]|[58][2-8]|6[2-7]|7[2-9]|9[1-9])|(?:2[2-9]|[36][1-9])\d|4(?:[2-578]\d|6[02-8]|9[2-59])|5(?:[2-589]\d|6[1-9]|7[2-8])|7(?:[25-9]\d|3[4-9]|4[02-9])|8(?:[2679]\d|3[2-9]|4[5-9]|5[1-9]|8[03-9])|9(?:[2-58]\d|[679][1-9]))\d{6}|(?:60\d|[7-9]0[1-9])\d{7}|00777(?:[01]|5\d)\d\d|(?:00(?:7778|882[1245])|(?:120|800\d)\d\d)\d{4}|00(?:37|66|78)\d{6,13}|990\d{6}|60\d{7}|50[1-9]\d{7}|20\d{8}|570\d{6})$/.test(
          normalizedValue
        )
      }
    )

export const numberSelectEmptyStringToNull = () =>
  yup
    .number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string' && originalValue === '') {
        return null
      }
      return value
    })
    .nullable()

export const textHiragana = () =>
  textRequired().matches(/^[ぁ-んー]+$/, {
    message: i18n.t('validation.ひらがなを入力してください'),
  })

export const textAlphabet = () =>
  yup
    .string()
    .trim()
    .matches(/^[a-zA-Z ]+$/, {
      message: i18n.t('validation.半角英字を入力してください'),
    })

export const textPassword = () =>
  yup
    .string()
    .required()
    .min(8)
    .max(100)
    .matches(
      /^[0-9a-zA-Z]+$/,
      i18n.t('validation.半角英数字を入力してください')
    )

export const textNewPassword = (fieldNameToCompare: string) =>
  textPassword()
    .matches(/[0-9]+/, i18n.t('validation.数字が含まれていません'))
    .matches(/[a-zA-Z]+/, i18n.t('validation.アルファベットが含まれていません'))
    .when(fieldNameToCompare, (oldPass: string, schema: any) =>
      schema.test({
        test: (newPass: string) => newPass && oldPass !== newPass,
        message: i18n.t('validation.異なるパスワードを入力して下さい'),
      })
    )

export const passCode = () =>
  yup
    .string()
    .required()
    .matches(/^[0-9]{6}$/, i18n.t('validation.6桁の数字を入力してください'))

export const email = () =>
  text()
    .email()
    .required()
    .test(
      'is-carrier-mail',
      i18n.t('validation.正しい形式にしてください'),
      (string) => {
        if (typeof string !== 'string') return false
        const domain = string.split('@')[1]
        const excludeDomains = ['docomo.ne.jp', 'ezweb.ne.jp', 'softbank.ne.jp']
        return !excludeDomains.includes(domain)
      }
    )
    .matches(/^([\x20-\x7e]+)?$/, i18n.t('validation.正しい形式にしてください'))

const validationSchema = {
  text,
  textHiragana,
  textAlphabet,
  textRequired,
  textPassword,
  textNewPassword,
  postalCode,
  phone,
  smsPhone,
  numberSelectEmptyStringToNull,
  passCode,
  email,
}

export default validationSchema
